<script setup lang="ts">
import { type Bid, BidType } from "mws-ts-v2-sdk";

type Props = {
  bid: Bid;
  bidderIsUser: boolean;
};

const { bid } = defineProps<Props>();

const { locale } = useI18n();

const bidFullDate = computed(() => {
  if (typeof bid.bidReceivedAt === "string") return "";

  return bid.bidReceivedAt.toLocaleDateString(locale.value, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    weekday: "long",
    hourCycle: "h23",
  });
});

const bidShortDate = computed(() => {
  if (typeof bid.bidReceivedAt === "string") return "";

  return bid.bidReceivedAt.toLocaleDateString(locale.value, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  });
});

const price = computed(() => {
  return bid.prices || [];
});

const { amount } = usePrice(price);
</script>

<template>
  <tr
    v-if="bid"
    class="flex flex-row items-center gap-2"
  >
    <td class="flex flex-row items-center gap-2 w-28 md:w-[86px] xl:w-28">
      <BaseFlagIcon
        v-if="bid.userCountryIso"
        :iso="bid.userCountryIso"
        :size="16"
      />
      <p
        class="text-xs font-semibold"
        data-cy="product-bid-bidder"
      >
        {{ bidderIsUser ? $t("common.you") : bid.obfuscatedUserId }}
      </p>
    </td>

    <td class="flex gap-1 items-center">
      <BaseTooltip class="-mt-[4px]">
        <p>{{ bidFullDate }}</p>
        <template #hover>
          <p class="text-xs text-platform-text-2 cursor-default mr-1">
            {{ bidShortDate }}
          </p>
        </template>
      </BaseTooltip>

      <BaseIcon
        v-if="bid.bidType === BidType.AutomaticBid"
        name="automatic"
        :size="14"
        class="text-platform-text-2"
        :alt="$t('product.automaticBid')"
      />

      <BaseTooltip
        v-if="bid.highestTiedBid"
        :alt="$t('product.tiedBid.alt')"
      >
        <template #title>
          {{ $t("product.tiedBid.title") }}
        </template>
        {{ $t("product.tiedBid.description") }}
      </BaseTooltip>
    </td>

    <td class="ml-auto text-right">
      <BaseCurrency
        class="text-xs font-semibold"
        data-cy="product-bid-price"
        :iso="amount.currencyCode"
        :value="amount.value"
        currency-size="xs"
      />
    </td>
  </tr>
</template>
